import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router';

import { AiMessageSourceRoute } from '@/config/router/routes';
import type { ContextChunkModel } from '@/domain/models/contextChunkModel';
import { useL10n } from '@/l10n/useL10n';
import { MaybeHero } from '@/presentation/widgets/MaybeHero';
import { PrimaryIconButton } from '@/presentation/widgets/PrimaryIconButton';
import { h } from '@/utils/screenUtil';

import styles from './AiMessageSourcePage.module.css';

/**
 * Перенос `lib/src/presentation/pages/ai_message_source_page/ai_message_source_page.dart`.
 *
 * Панель источников ответа: список фрагментов книг поверх чата. Страница
 * прозрачная, чат виден под ней, а сама панель прилетает с Hero-тегом,
 * который приходит в аргументах маршрута.
 */

/** `double get gradientHeight => 182.h` */
const GRADIENT_HEIGHT = 182;

/** `PrimaryIconButton(size: 64, iconSize: 24)` */
const CLOSE_BUTTON_SIZE = 64;
const CLOSE_ICON_SIZE = 24;

/** `Colors.transparent` и `Colors.black`. */
const TRANSPARENT = 'transparent';
const BLACK = '#000000';

/**
 * `_closed` + `mounted` из Dart-версии: переход назад делается один раз.
 *
 * Флаг взводится и при размонтировании — это тот же `mounted`, по которому
 * Dart решал, можно ли ещё дёргать `context.pop()`.
 */
function useCloseOnce(): () => void {
  const navigate = useNavigate();
  const closedRef = useRef(false);

  useEffect(() => {
    return () => {
      closedRef.current = true;
    };
  }, []);

  return useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    navigate(-1);
  }, [navigate]);
}

/** `_scrollListener` + `ValueNotifier<bool> _showGradient`. */
function useGradientVisibility(scrollRef: RefObject<HTMLDivElement | null>): {
  readonly showGradient: boolean;
  readonly updateGradient: () => void;
} {
  const [showGradient, setShowGradient] = useState(true);

  const updateGradient = useCallback(() => {
    const element = scrollRef.current;
    // `if (_scrollController.hasClients)` — без прикреплённой прокрутки
    // значение не трогаем.
    if (element === null) return;
    const maxScrollExtent = element.scrollHeight - element.clientHeight;
    setShowGradient(
      maxScrollExtent > 0 &&
        element.scrollTop <= maxScrollExtent - h(GRADIENT_HEIGHT),
    );
  }, [scrollRef]);

  // `WidgetsBinding.instance.addPostFrameCallback` — первый замер после
  // раскладки: `useEffect` тоже срабатывает уже по готовому DOM.
  useEffect(() => {
    updateGradient();
  }, [updateGradient]);

  return { showGradient, updateGradient };
}

/**
 * `TapRegion(onTapOutside: ...)`.
 *
 * Во Flutter поведение по умолчанию — `deferToChild`: «внутри» считается то,
 * что попало в саму панель, отступы вокруг неё уже снаружи. Поэтому здесь
 * проверяется попадание в элемент панели, а не в обёртку с отступами.
 */
function useTapOutside(
  panelRef: RefObject<HTMLElement | null>,
  onTapOutside: () => void,
): void {
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent): void => {
      const panel = panelRef.current;
      if (panel === null) return;
      if (event.target instanceof Node && panel.contains(event.target)) return;
      onTapOutside();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [panelRef, onTapOutside]);
}

export interface SourceHeaderProps {
  readonly onClose: () => void;
}

/** Ряд шапки: заголовок и крестик. */
export function SourceHeader({ onClose }: SourceHeaderProps) {
  const l10n = useL10n();
  return (
    <div className={styles.header}>
      <span className={styles.title}>{l10n.source}</span>
      <PrimaryIconButton
        className={styles.closeButton}
        overlayColor={TRANSPARENT}
        onPressed={onClose}
        size={CLOSE_BUTTON_SIZE}
        backgroundColor={TRANSPARENT}
        icon="cancel"
        iconSize={CLOSE_ICON_SIZE}
        foregroundColor={BLACK}
      />
    </div>
  );
}

export interface SourceChunkTileProps {
  readonly chunk: ContextChunkModel;
}

/** `itemBuilder`: книга, страница и сам фрагмент. */
export function SourceChunkTile({ chunk }: SourceChunkTileProps) {
  const l10n = useL10n();
  return (
    <div className={styles.chunk}>
      <span className={styles.book}>{`${l10n.book}: ${chunk.book}`}</span>
      <span className={styles.pageNumber}>
        {`${l10n.page_number} ${chunk.pageNumber}`}
      </span>
      <span className={styles.answer}>{chunk.answer}</span>
    </div>
  );
}

export interface SourceChunkListProps {
  readonly chunks: readonly ContextChunkModel[];
  readonly scrollRef: RefObject<HTMLDivElement | null>;
  readonly onScroll: () => void;
}

/** `CustomScrollView` + `SliverList.separated`. */
export function SourceChunkList({
  chunks,
  scrollRef,
  onScroll,
}: SourceChunkListProps) {
  return (
    <div className={styles.scroll} ref={scrollRef} onScroll={onScroll}>
      <div className={styles.list}>
        {chunks.map((chunk, index) => (
          // Ключ по номеру в списке: порядок фрагментов неизменен, своего
          // идентификатора у них нет — как и у `itemBuilder` в Dart.
          <SourceChunkTile key={index} chunk={chunk} />
        ))}
      </div>
    </div>
  );
}

export function AiMessageSourcePage() {
  const args = AiMessageSourceRoute.useArgs();
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const close = useCloseOnce();
  const { showGradient, updateGradient } = useGradientVisibility(scrollRef);

  useTapOutside(panelRef, close);

  // Случай, которого в Dart быть не могло: в go_router сообщение приходило
  // в `$extra` вместе с переходом, а в вебе адрес можно открыть напрямую —
  // тогда показывать нечего и мы уходим назад.
  useEffect(() => {
    if (args === null) close();
  }, [args, close]);

  if (args === null) return null;

  const gradientClassName = showGradient
    ? `${styles.gradient} ${styles.gradientVisible}`
    : styles.gradient;

  return (
    <div className={styles.root}>
      <MaybeHero tag={args.hero} className={styles.hero}>
        <div className={styles.padding}>
          <div className={styles.panel} ref={panelRef}>
            <SourceHeader onClose={close} />
            <div className={styles.body}>
              <SourceChunkList
                chunks={args.message.chunks}
                scrollRef={scrollRef}
                onScroll={updateGradient}
              />
              <div className={gradientClassName} />
            </div>
          </div>
        </div>
      </MaybeHero>
    </div>
  );
}
